import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Services.css"; // shared tokens + hero

import heroBg from "../../assets/images/services/Design-and-deployment-Hero.png";

const steps = [
  {
    t: "1 — Discover & Requirements",
    d: "Gather goals, constraints, sites, users, apps, and risk/compliance needs.",
  },
  {
    t: "2 — Design & Validation",
    d: "Create architecture, IP plan, segmentation, Wi‑Fi model, and redundancy approach.",
  },
  {
    t: "3 — Build & Configure",
    d: "Configure switching, wireless, firewall/VPN, and security baselines in a controlled plan.",
  },
  {
    t: "4 — Deploy & Cutover",
    d: "Execute staged deployment with minimal downtime, then validate services end-to-end.",
  },
  {
    t: "5 — Document & Handover",
    d: "Provide diagrams, runbooks, and admin knowledge transfer for long‑term support.",
  },
];

const benefits = [
  {
    t: "More reliability",
    d: "Redundancy, validated configs, and clean cutovers reduce outages and instability.",
  },
  {
    t: "Better performance",
    d: "Optimized Wi‑Fi and switching design improves throughput for real workloads.",
  },
  {
    t: "Cleaner operations",
    d: "Documentation and standards make future changes, audits, and troubleshooting faster.",
  },
];

const faqs = [
  {
    q: "How long does a typical deployment take?",
    a: "Timelines depend on sites, hardware readiness, cabling, and cutover constraints; a rollout plan is defined after discovery and design.",
  },
  {
    q: "Can deployments be done after business hours?",
    a: "Yes — cutovers can be scheduled to reduce disruption, with validation and rollback planning.",
  },
  {
    q: "Do you provide documentation?",
    a: "Yes — diagrams, IP plans, runbooks, and a handover session are included to support operations.",
  },
];

const DesignAndDeployment = () => {
  const navigate = useNavigate();

  // Step cards pop-in
  const cardRefs = useRef([]);
  useEffect(() => {
    const els = cardRefs.current.filter(Boolean);
    if (!els.length) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in");
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.18 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  // CTA reveal
  const ctaRef = useRef(null);
  const [ctaInView, setCtaInView] = useState(false);
  useEffect(() => {
    const el = ctaRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { setCtaInView(true); obs.unobserve(el); }
      },
      { threshold: 0.2 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Benefits reveal
  const benefitsRef = useRef(null);
  const [benefitsInView, setBenefitsInView] = useState(false);
  useEffect(() => {
    const el = benefitsRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setBenefitsInView(true); },
      { threshold: 0.2 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div className="servicePage">

      {/* ── HERO ──────────────────────────────────────────────── */}
      <section className="hero-section">
        <div className="hero-section__grid-bg" aria-hidden="true" />

        <div className="hero-left">
          <div className="dd-serviceLabel">
            <span className="live-dot" aria-hidden="true" />
            Design and Deployment
          </div>

          <h1 className="hc-hero__title">
            Pave the Way for Robust<br />
            Network Infrastructure
          </h1>

          <p className="hc-hero__lead">
            Unlock reliable performance with customised design and seamless deployment of
            network solutions tailored to your business objectives, security needs, and
            growth plans.
          </p>

          <button className="btn-orange" type="button" onClick={() => navigate("/contact")}>
            Meet Our Experts Today
            <svg
              className="btn-arrow"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        <div className="hero-right">
          <div className="hero-image-container">
            <img className="hero-img" src={heroBg} alt="Network design and deployment" />
            <span className="scan-corner tl" aria-hidden="true" />
            <span className="scan-corner tr" aria-hidden="true" />
            <span className="scan-corner bl" aria-hidden="true" />
            <span className="scan-corner br" aria-hidden="true" />
          </div>
        </div>
      </section>

      {/* ── OVERVIEW ──────────────────────────────────────────── */}
      <section className="dd-overview">
        <div className="dd-overview__container">
          <div className="dd-overview__grid">

            <div className="dd-overview__text">
              <p className="section-eyebrow">
                <span className="live-dot" aria-hidden="true" />
                Overview
              </p>
              <h2 className="dd-h2">Build a network foundation that supports growth</h2>
              <p className="dd-body">
                A strong network is the backbone of day‑to‑day operations — supporting
                productivity, security, voice/video, cloud services, and future expansion.
                This service helps organisations plan and deploy network infrastructure with
                fewer surprises and a cleaner long‑term support path.
              </p>
              <h3 className="dd-h3">What to expect</h3>
              <p className="dd-body">
                Start with requirements and site constraints, then move into design,
                implementation, testing, and handover. The goal is a stable, secure setup
                that your team can run with confidence.
              </p>
              <p className="dd-body dd-body--wide">
                Typical outcomes include improved reliability, clearer segmentation and
                security controls, optimised coverage and throughput, and documentation that
                makes changes and troubleshooting significantly faster.
              </p>
            </div>

            <div className="dd-overview__media">
              <div className="dd-overview__imgWrap">
                <img
                  src="https://images.unsplash.com/photo-1551703599-6b3e8379aa8f?auto=format&fit=crop&w=1400&q=80"
                  alt="Network implementation"
                  className="dd-overview__img"
                />
                <span className="scan-corner tl" aria-hidden="true" />
                <span className="scan-corner tr" aria-hidden="true" />
                <span className="scan-corner bl" aria-hidden="true" />
                <span className="scan-corner br" aria-hidden="true" />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── DELIVERY PROCESS ──────────────────────────────────── */}
      <section className="dd-process">
        <div className="dd-process__container">

          <div className="dd-process__header">
            <p className="section-eyebrow section-eyebrow--center">
              <span className="live-dot" aria-hidden="true" />
              Delivery workflow
            </p>
            <h2 className="dd-h2 dd-h2--center">How delivery works</h2>
            <p className="dd-process__lead">
              A clear end‑to‑end workflow — requirements, design, implementation, testing,
              and handover — so you go live with confidence and predictable operations.
            </p>
          </div>

          <div className="dd-steps">
            {steps.map((s, i) => (
              <div
                key={s.t}
                ref={(el) => (cardRefs.current[i] = el)}
                className="dd-step"
              >
                <span className="dd-step__num" aria-hidden="true">{`0${i + 1}`}</span>
                <h3 className="dd-step__title">{s.t}</h3>
                <p className="dd-step__text">{s.d}</p>
              </div>
            ))}
          </div>

          {/* Deliverables */}
          <div className="dd-deliverables">
            <p className="section-eyebrow">
              <span className="live-dot" aria-hidden="true" />
              Deliverables
            </p>
            <h3 className="dd-h3">Typical deliverables</h3>
            <ul className="dd-deliverables__list">
              <li>Network diagrams (logical + physical) and inventory.</li>
              <li>IP plan, VLAN/segmentation model, and routing notes.</li>
              <li>Firewall policy baseline + VPN configuration details.</li>
              <li>Wi‑Fi coverage assumptions, SSIDs, and security settings.</li>
              <li>Testing checklist + go‑live validation results.</li>
              <li>Handover pack: runbooks, admin guides, and escalation paths.</li>
            </ul>
            <button
              className="dd-outlineBtn"
              type="button"
              onClick={() => navigate("/contact")}
            >
              Get a Deployment Plan
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12h14M13 5l7 7-7 7" />
              </svg>
            </button>
          </div>

        </div>
      </section>

      {/* ── CTA BANNER ────────────────────────────────────────── */}
      <section className="dd-cta-section">
        <div
          ref={ctaRef}
          className={`dd-cta-card${ctaInView ? " in" : ""}`}
        >
          <div className="dd-cta-left">
            <img
              className="dd-cta-illustration"
              src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1400&q=80"
              alt="Deployment support team"
            />
            <span className="scan-corner tl" aria-hidden="true" />
            <span className="scan-corner tr" aria-hidden="true" />
            <span className="scan-corner bl" aria-hidden="true" />
            <span className="scan-corner br" aria-hidden="true" />
          </div>

          <div className="dd-cta-right">
            <p className="section-eyebrow">
              <span className="live-dot" aria-hidden="true" />
              Get started
            </p>
            <h2 className="dd-cta-heading">
              Upgrade your network<br />
              with <span className="dd-cta-heading__accent">ALG Data Guard</span>
            </h2>
            <p className="dd-cta-body">
              Get a dependable design and a clean deployment plan — then go live with
              testing, documentation, and support that makes operations easier.
            </p>
            <button className="btn-orange" type="button" onClick={() => navigate("/contact")}>
              Meet Our Experts Today
              <svg
                className="btn-arrow"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M5 12h14M13 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* ── BENEFITS ──────────────────────────────────────────── */}
      <section className="dd-benefits">
        <div className="dd-benefits__container" ref={benefitsRef}>

          <div className={`dd-benefits__left${benefitsInView ? " in" : ""}`}>
            <p className="section-eyebrow">
              <span className="live-dot" aria-hidden="true" />
              Why it matters
            </p>
            <h2 className="dd-h2">Why design + deployment matters</h2>
            <div className="dd-benefits__rule" aria-hidden="true" />
            <p className="dd-body">
              A well‑designed network reduces downtime, improves user experience, and keeps
              security controls consistent across sites and teams.
            </p>
          </div>

          <div className={`dd-benefits__right${benefitsInView ? " in" : ""}`}>
            {benefits.map((b) => (
              <div key={b.t} className="dd-benefit-block">
                <h3 className="dd-h3">{b.t}</h3>
                <p className="dd-body">{b.d}</p>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────── */}
      <section className="dd-faq">
        <div className="dd-faq__container">
          <p className="section-eyebrow">
            <span className="live-dot" aria-hidden="true" />
            FAQ
          </p>
          <h2 className="dd-h2">Frequently asked questions</h2>

          <div className="dd-faq__list">
            {faqs.map((f) => (
              <details key={f.q} className="dd-faqItem">
                <summary className="dd-faqItem__summary">{f.q}</summary>
                <p className="dd-faqItem__answer">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
};

export default DesignAndDeployment;